import React, { useEffect, useMemo, useRef, useState } from "react";

import { Search } from "lucide-react";

import { planCardCta, USER_AWARENESS_NOTE } from "./actionPlans";
import CardPreview from "./CardPreview";
import Drawer from "./Drawer";
import { draftMappedTarget, draftTargets, REVIEW_META } from "./targets";
import {
  draftParameterValues,
  draftTier,
  type CardCopy,
  type CtaOverride,
  type SignalAdminDraft,
  type SignalAdminRegistry,
  type SignalDefinition,
  type TargetRecord,
} from "./types";
import Button from "../../Components/Button";
import {
  defaultCardCopy,
  templateErrors,
} from "../../Pages/MyPortfolioAccounts/signalCTAs";

interface CtaPanelProps {
  registry: SignalAdminRegistry;
  draft: SignalAdminDraft;
  /** The applied config's overrides, to mark staged rows. */
  appliedCtas: Record<string, CtaOverride>;
  onDraftChange: (next: SignalAdminDraft) => void;
  /** Show only definitions mapped to this target (from Target Registry). */
  targetFilter?: string;
  onClearTargetFilter?: () => void;
  /** Open this signal's Action Guide in the Action Guides editor. */
  onEditGuide?: (definitionId: string) => void;
}

/** Mirrors _cta_errors() in the backend (the CTA review pack's rules). */
const GENERIC_LABELS = new Set([
  "synopsis",
  "synopsis →",
  "open",
  "take action",
  "view",
  "go",
]);

export const ctaErrors = (
  cta: CtaOverride,
  targets: Map<string, TargetRecord>,
  /** The target this definition maps to today — keeping it is always
   * allowed; a new or changed mapping needs a confirmed, enabled target. */
  currentTarget: string
): Partial<Record<keyof CtaOverride, string>> => {
  const errors: Partial<Record<keyof CtaOverride, string>> = {};
  const label = cta.label.trim();
  if (!label) errors.label = "A label is required.";
  else if (label.length < 4) errors.label = "Use at least four characters.";
  else if (GENERIC_LABELS.has(label.toLowerCase()))
    errors.label = "Name the action — generic labels aren't allowed.";
  const target = targets.get(cta.target);
  if (!target?.kind)
    errors.target = "Choose a registered target ExecBridge can route to.";
  else if (cta.target !== currentTarget && !target.accepts_new_mappings)
    errors.target =
      "New or changed mappings need a confirmed, enabled target — confirm it in Target Registry first.";
  if (!cta.destination.trim())
    errors.destination = "A route label is required.";
  if (!cta.permission.trim())
    errors.permission = "A permission boundary is required.";
  if (!cta.resolution.trim())
    errors.resolution = "A resolution expectation is required.";
  return errors;
};

const isGenericDefault = (d: SignalDefinition) =>
  GENERIC_LABELS.has(d.cta_default.label.toLowerCase());

const inputClass = (invalid: boolean) =>
  `w-full rounded-lg border px-3 py-2 text-[13px] text-gray-900 focus:outline-none focus:ring-2 ${
    invalid
      ? "border-red-400 focus:ring-red-200"
      : "border-gray-300 focus:border-brand focus:ring-brand/20"
  }`;

export const CtaEditor: React.FC<{
  definition: SignalDefinition;
  registry: SignalAdminRegistry;
  draft: SignalAdminDraft;
  onDraftChange: (next: SignalAdminDraft) => void;
  onClose: () => void;
  onEditGuide?: (definitionId: string) => void;
  /** Render inside another drawer (Registry's Card
   * tab) instead of opening its own. */
  embedded?: boolean;
}> = ({
  definition,
  registry,
  draft,
  onDraftChange,
  onClose,
  onEditGuide,
  embedded = false,
}) => {
  const staged = draft.ctas[definition.id];
  const targets = useMemo(
    () => new Map(draftTargets(registry, draft).map(t => [t.id, t])),
    [registry, draft]
  );
  const currentTarget = draftMappedTarget(registry, draft, definition.id);
  const applied =
    definition.cta_effective.source === "override"
      ? definition.cta_effective
      : null;
  const seed: CtaOverride = staged ?? {
    label: applied?.label ?? definition.cta,
    target:
      applied?.target ??
      (targets.get(definition.target)?.kind ? definition.target : ""),
    destination: applied?.destination ?? definition.destination,
    permission: applied?.permission ?? "Assigned account team",
    resolution: applied?.resolution ?? definition.resolution,
  };
  // An approved action plan replaces this button on users' cards (the
  // backend's effective_cta checks the plan first), so the preview and the
  // label field must say so rather than show a card nobody gets.
  const approvedPlan = draft.action_plans?.[definition.id];
  const planCta =
    approvedPlan?.status === "approved" && approvedPlan.steps.length
      ? planCardCta(approvedPlan.steps)
      : null;
  const [form, setForm] = useState<CtaOverride>(seed);
  const [touched, setTouched] = useState(false);

  // Card text: shown filled with the default template until edited; only
  // fields that differ from the default are stored.
  const defaults = defaultCardCopy(definition.key, definition.variant);
  const copySeed = {
    title: draft.card_copy[definition.id]?.title ?? defaults.title,
    text: draft.card_copy[definition.id]?.text ?? defaults.text,
    why: draft.card_copy[definition.id]?.why ?? defaults.why,
  };
  const [copyForm, setCopyForm] = useState(copySeed);
  const copyRefs = {
    title: useRef<HTMLInputElement>(null),
    text: useRef<HTMLTextAreaElement>(null),
    why: useRef<HTMLInputElement>(null),
  };
  const [lastCopyField, setLastCopyField] =
    useState<keyof typeof copySeed>("text");
  useEffect(() => {
    setForm(seed);
    setCopyForm(copySeed);
    setTouched(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [definition.id]);

  const COPY_LIMITS = { title: 80, text: 400, why: 200 } as const;
  const copyErrors: Partial<Record<keyof typeof copySeed, string>> = {};
  (Object.keys(COPY_LIMITS) as (keyof typeof copySeed)[]).forEach(key => {
    const value = copyForm[key].trim();
    const problems = templateErrors(value, definition.placeholders);
    if (value.length > COPY_LIMITS[key])
      problems.push(`Keep it under ${COPY_LIMITS[key]} characters.`);
    if (key === "title" && value.length < 3)
      problems.push("A title is required.");
    if (problems.length) copyErrors[key] = problems.join(" ");
  });
  // Placeholders that can be empty for this signal render as "Unknown" — allowed,
  // but flagged so the admin decides knowingly.
  const nullableUsed = definition.nullable_placeholders.filter(name =>
    Object.values(copyForm).some(v => v.includes(`{{${name}}}`))
  );
  const [showMissing, setShowMissing] = useState(false);
  const copyOverride: CardCopy = {};
  (Object.keys(COPY_LIMITS) as (keyof typeof copySeed)[]).forEach(key => {
    const value = copyForm[key].trim();
    if (value && value !== defaults[key]) copyOverride[key] = value;
  });

  /** Insert a placeholder where the cursor is in the last card-text field. */
  const insertPlaceholder = (name: string) => {
    const el = copyRefs[lastCopyField].current;
    const token = `{{${name}}}`;
    const value = copyForm[lastCopyField];
    const start = el?.selectionStart ?? value.length;
    const end = el?.selectionEnd ?? value.length;
    setCopyForm(prev => ({
      ...prev,
      [lastCopyField]: value.slice(0, start) + token + value.slice(end),
    }));
    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(start + token.length, start + token.length);
    });
  };

  const errors = ctaErrors(form, targets, currentTarget);
  const valid = Object.keys(errors).length === 0;
  const set = (field: keyof CtaOverride, value: string) =>
    setForm(prev => ({ ...prev, [field]: value }));
  const selected = targets.get(form.target);
  // Confirmed, enabled targets — plus the one it maps to today, which may
  // still be a legacy reference under review.
  const targetOptions = Array.from(targets.values()).filter(
    t => t.kind && (t.accepts_new_mappings || t.id === currentTarget)
  );

  // A CTA override is written only if the CTA itself was edited (or one
  // is already staged), so editing just the card text never forces one.
  const ctaChanged = !!staged || JSON.stringify(form) !== JSON.stringify(seed);
  const copyValid = Object.keys(copyErrors).length === 0;

  const save = () => {
    setTouched(true);
    if ((ctaChanged && !valid) || !copyValid) return;
    const nextCopy = { ...draft.card_copy };
    if (Object.keys(copyOverride).length)
      nextCopy[definition.id] = copyOverride;
    else delete nextCopy[definition.id];
    onDraftChange({
      ...draft,
      ctas: ctaChanged
        ? {
            ...draft.ctas,
            [definition.id]: { ...form, label: form.label.trim() },
          }
        : draft.ctas,
      card_copy: nextCopy,
    });
    onClose();
  };

  const resetToDefault = () => {
    const next = { ...draft.ctas };
    delete next[definition.id];
    const nextCopy = { ...draft.card_copy };
    delete nextCopy[definition.id];
    onDraftChange({ ...draft, ctas: next, card_copy: nextCopy });
    onClose();
  };

  const field = (
    key: keyof CtaOverride,
    label: string,
    input: React.ReactNode
  ) => (
    <label className="flex flex-col gap-1">
      <span className="text-[11px] font-bold uppercase tracking-[0.06em] text-gray-500">
        {label}
      </span>
      {input}
      {touched && ctaChanged && errors[key] && (
        <span className="text-[11.5px] text-red-600">{errors[key]}</span>
      )}
    </label>
  );

  const body = (
    <>
      <div className="mb-5 grid grid-cols-2 gap-3 rounded-lg border border-gray-200 bg-gray-50/60 p-3 text-[12px]">
        <div>
          <div className="text-[10.5px] font-bold uppercase tracking-[0.06em] text-gray-400">
            Users see now
          </div>
          <div className="mt-1 font-semibold text-gray-900">
            {definition.cta_effective.label}
          </div>
          <div className="text-gray-500">
            {definition.cta_effective.source === "override"
              ? "Izba-admin override"
              : `ExecBridge default → ${definition.cta_default.destination}`}
          </div>
        </div>
        <div>
          <div className="text-[10.5px] font-bold uppercase tracking-[0.06em] text-gray-400">
            Registry suggests
          </div>
          <div className="mt-1 font-semibold text-gray-900">
            {definition.cta}
          </div>
          <div className="text-gray-500">{definition.destination}</div>
        </div>
      </div>

      <section className="mb-5 flex flex-col gap-3 rounded-lg border border-gray-200 p-3">
        <div>
          <h3 className="text-[10.5px] font-bold uppercase tracking-[0.08em] text-gray-500">
            Card text
          </h3>
          <p className="mt-0.5 text-[11.5px] text-gray-500">
            What the card says above the button. Placeholders are filled from
            each account&apos;s values; this signal can use:
          </p>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {definition.placeholders.length ? (
              definition.placeholders.map(name => {
                const nullable =
                  definition.nullable_placeholders.includes(name);
                return (
                  <button
                    key={name}
                    type="button"
                    onClick={() => insertPlaceholder(name)}
                    title={
                      nullable
                        ? `Insert {{${name}}} — can be empty for this signal — it then reads ${name === "al" || name === "rl" ? "“?”" : "“unknown”"}`
                        : `Insert {{${name}}}`
                    }
                    className={`rounded border px-1.5 py-0.5 font-mono text-[11px] hover:border-brand hover:text-brand ${
                      nullable
                        ? "border-amber-300 bg-amber-50 text-amber-800"
                        : "border-gray-200 bg-gray-50 text-gray-700"
                    }`}
                  >
                    {`{{${name}}}`}
                    {nullable ? " *" : ""}
                  </button>
                );
              })
            ) : (
              <span className="text-[11.5px] text-gray-400">
                No placeholders for this signal.
              </span>
            )}
          </div>
        </div>
        {definition.nullable_placeholders.length > 0 && (
          <p className="-mt-1 text-[11px] text-amber-800">
            * Can be empty when this signal fires: a level then reads “?” (e.g.
            “AL ?”), anything else “unknown”, instead of a number.
            {nullableUsed.length > 0 &&
              ` You're using ${nullableUsed.map(n => `{{${n}}}`).join(", ")} — check the preview with missing values.`}
          </p>
        )}
        {(
          [
            ["title", "Title"],
            ["text", "Message"],
            ["why", "Why line (“Surfaced because: …”)"],
          ] as const
        ).map(([key, label]) => (
          <label key={key} className="flex flex-col gap-1">
            <span className="flex items-center justify-between text-[11px] font-bold uppercase tracking-[0.06em] text-gray-500">
              {label}
              {copyForm[key].trim() !== defaults[key] && (
                <button
                  type="button"
                  onClick={() =>
                    setCopyForm(prev => ({ ...prev, [key]: defaults[key] }))
                  }
                  className="text-[11px] font-semibold normal-case tracking-normal text-brand hover:underline"
                >
                  Use default
                </button>
              )}
            </span>
            {key === "text" ? (
              <textarea
                ref={copyRefs.text}
                value={copyForm.text}
                onFocus={() => setLastCopyField("text")}
                onChange={e =>
                  setCopyForm(prev => ({ ...prev, text: e.target.value }))
                }
                rows={3}
                className={inputClass(!!copyErrors.text)}
              />
            ) : (
              <input
                ref={key === "title" ? copyRefs.title : copyRefs.why}
                value={copyForm[key]}
                onFocus={() => setLastCopyField(key)}
                onChange={e =>
                  setCopyForm(prev => ({ ...prev, [key]: e.target.value }))
                }
                className={inputClass(!!copyErrors[key])}
              />
            )}
            {copyErrors[key] && (
              <span className="text-[11.5px] text-red-600">
                {copyErrors[key]}
              </span>
            )}
          </label>
        ))}
      </section>

      <div className="flex flex-col gap-3.5">
        {field(
          "label",
          "Button label",
          <input
            value={form.label}
            onChange={e => set("label", e.target.value)}
            className={inputClass(touched && !!errors.label)}
          />
        )}
        {planCta && (
          <p className="-mt-2 m-0 rounded-md bg-amber-50 px-2.5 py-1.5 text-[11.5px] text-amber-800">
            Users don&apos;t see this button: this signal&apos;s approved action
            plan sets it to <strong>{planCta.label}</strong>. Change it in
            Target Registry → Signal actions. This label comes back only if the
            plan stops being approved.
          </p>
        )}
        {field(
          "target",
          "Destination",
          <select
            value={form.target}
            onChange={e => set("target", e.target.value)}
            className={inputClass(touched && !!errors.target)}
          >
            <option value="">Choose…</option>
            {targetOptions.map(t => (
              <option key={t.id} value={t.id}>
                {t.label}
                {t.review_status === "confirmed"
                  ? ""
                  : " — legacy · needs review"}
              </option>
            ))}
          </select>
        )}
        {selected && (
          <div className="-mt-1.5 rounded-lg border border-gray-200 bg-gray-50/70 px-3 py-2 text-[11.5px] text-gray-600">
            <span className="font-semibold text-gray-900">
              {selected.label}
            </span>{" "}
            · {REVIEW_META[selected.review_status].label} ·{" "}
            {selected.available ? "Enabled" : "Disabled"}
            <div className="mt-0.5">
              Route:{" "}
              <span className="font-mono">
                {selected.route ||
                  (selected.kind === "assess"
                    ? "opens the AL assessment in place"
                    : selected.kind === "brief"
                      ? "opens the Pre-call Brief in place"
                      : "none")}
              </span>{" "}
              · {selected.permission || "No permission boundary"}
            </div>
          </div>
        )}
        {field(
          "destination",
          "Route label",
          <input
            value={form.destination}
            onChange={e => set("destination", e.target.value)}
            className={inputClass(touched && !!errors.destination)}
          />
        )}
        {field(
          "permission",
          "Permission boundary",
          <input
            value={form.permission}
            onChange={e => set("permission", e.target.value)}
            className={inputClass(touched && !!errors.permission)}
          />
        )}
        {field(
          "resolution",
          "Resolution expectation",
          <input
            value={form.resolution}
            onChange={e => set("resolution", e.target.value)}
            className={inputClass(touched && !!errors.resolution)}
          />
        )}
      </div>

      {onEditGuide && (
        <div className="mt-5 flex flex-wrap items-center gap-3 rounded-lg border border-gray-200 bg-gray-50/60 px-3 py-2.5 text-[12px] text-gray-600">
          <span className="min-w-0 flex-1">
            <span className="font-semibold text-gray-900">Action guide:</span>{" "}
            {draft.guides[definition.id]?.live
              ? "Live — this button opens the guide first."
              : draft.guides[definition.id]
                ? "Saved, not shown on cards."
                : registry.guide_suggestions[definition.id]
                  ? "ExecBridge default — this button opens the default guide first."
                  : "None — the button goes straight to its target."}
          </span>
          <button
            type="button"
            onClick={() => onEditGuide(definition.id)}
            className="text-[12px] font-semibold text-brand hover:underline"
          >
            {draft.guides[definition.id] ? "Edit guide" : "Create guide"}
          </button>
        </div>
      )}

      <section className="mt-6">
        <h3 className="mb-2 text-[10.5px] font-bold uppercase tracking-[0.08em] text-gray-400">
          What users see · example values
        </h3>
        {definition.nullable_placeholders.length > 0 && (
          <label className="mb-2 flex items-center gap-2 text-[11.5px] text-gray-600">
            <input
              type="checkbox"
              checked={showMissing}
              onChange={e => setShowMissing(e.target.checked)}
            />
            Preview an account where{" "}
            {definition.nullable_placeholders.map(n => `{{${n}}}`).join(", ")}{" "}
            is missing
          </label>
        )}
        <CardPreview
          definition={definition}
          parameters={draftParameterValues(registry, draft)}
          tier={draftTier(definition, draft)}
          missing={showMissing ? definition.nullable_placeholders : []}
          label={planCta ? planCta.label : form.label.trim()}
          detail={planCta?.detail ?? null}
          awarenessNote={
            planCta && approvedPlan?.awareness ? USER_AWARENESS_NOTE : null
          }
          copy={{
            title: copyForm.title.trim(),
            text: copyForm.text.trim(),
            why: copyForm.why.trim(),
          }}
        />
      </section>

      <div className="mt-6 flex flex-wrap items-center gap-2 border-t border-gray-100 pt-4">
        <Button variant="primary" onClick={save} disabled={!copyValid}>
          Stage changes
        </Button>
        {!embedded && (
          <Button variant="white" onClick={onClose}>
            Cancel
          </Button>
        )}
        {(staged || applied || draft.card_copy[definition.id]) && (
          <button
            type="button"
            onClick={resetToDefault}
            className="ml-auto text-[12px] font-semibold text-brand hover:underline"
          >
            Revert to ExecBridge default
          </button>
        )}
      </div>
    </>
  );
  if (embedded) return body;
  return (
    <Drawer
      open
      onClose={onClose}
      eyebrow={
        <span className="text-[10.5px] font-bold uppercase tracking-[0.08em] text-brand-dark">
          CTA draft
        </span>
      }
      title={definition.title}
      subtitle={definition.id}
    >
      {body}
    </Drawer>
  );
};

const CtaPanel: React.FC<CtaPanelProps> = ({
  registry,
  draft,
  appliedCtas,
  onDraftChange,
  targetFilter = "",
  onClearTargetFilter,
  onEditGuide,
}) => {
  const [editing, setEditing] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [onlyGeneric, setOnlyGeneric] = useState(false);
  const canonical = useMemo(
    () => registry.definitions.filter(d => d.definition_status === "canonical"),
    [registry.definitions]
  );
  const targets = useMemo(
    () => new Map(draftTargets(registry, draft).map(t => [t.id, t])),
    [registry, draft]
  );
  const filterTarget = targetFilter ? targets.get(targetFilter) : undefined;
  const rows = canonical.filter(d => {
    if (onlyGeneric && !isGenericDefault(d)) return false;
    if (
      targetFilter &&
      draftMappedTarget(registry, draft, d.id) !== targetFilter
    )
      return false;
    const q = query.trim().toLowerCase();
    return (
      !q ||
      [d.title, d.id, d.cta_effective.label].join(" ").toLowerCase().includes(q)
    );
  });
  const genericCount = canonical.filter(
    d => isGenericDefault(d) && d.cta_effective.source === "default"
  ).length;
  const editingDef = registry.definitions.find(d => d.id === editing);

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h3 className="text-[15px] font-bold text-gray-900">
          CTA Configuration
        </h3>
        <p className="mt-0.5 max-w-3xl text-[12.5px] text-gray-500">
          The action button on each signal card — its label, where it takes the
          user, and what should resolve it. Staged changes reach users only
          after you apply them.
        </p>
      </div>

      {genericCount > 0 && (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-[12.5px] text-amber-900">
          <span>
            <strong>{genericCount} signals</strong> still show a generic
            “Synopsis →” button that doesn&apos;t name an action.
          </span>
          <button
            type="button"
            onClick={() => setOnlyGeneric(v => !v)}
            className="ml-auto text-[12px] font-semibold text-amber-900 underline"
          >
            {onlyGeneric ? "Show all" : "Show only these"}
          </button>
        </div>
      )}

      {targetFilter && (
        <div className="flex flex-wrap items-center gap-2 text-[12.5px]">
          <span className="rounded-full bg-brand-tint px-2.5 py-0.5 font-semibold text-brand-dark">
            Target: {filterTarget?.label ?? targetFilter}
          </span>
          <span className="text-gray-500">
            {rows.length} mapped signal{rows.length === 1 ? "" : "s"}
          </span>
          {onClearTargetFilter && (
            <button
              type="button"
              onClick={onClearTargetFilter}
              className="text-[12px] font-semibold text-brand hover:underline"
            >
              Clear target filter
            </button>
          )}
        </div>
      )}

      <div className="relative w-full sm:w-[280px]">
        <Search
          className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
          aria-hidden
        />
        <input
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Search signals or labels…"
          className="h-9 w-full rounded-lg border border-gray-300 pl-8 pr-3 text-[12.5px] focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
        />
      </div>

      <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
        <table className="w-full min-w-[820px] border-collapse text-left">
          <thead>
            <tr className="bg-gray-50 text-[10px] font-bold uppercase tracking-[0.07em] text-gray-400">
              <th className="px-4 py-2">Signal</th>
              <th className="px-3 py-2">Button users see</th>
              <th className="px-3 py-2">Goes to</th>
              <th className="px-3 py-2">Review</th>
              <th className="px-4 py-2">Draft</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 text-[12.5px]">
            {rows.map(d => {
              const staged = draft.ctas[d.id];
              const appliedOverride = d.cta_effective.source === "override";
              const changed =
                JSON.stringify(staged ?? null) !==
                JSON.stringify(appliedCtas[d.id] ?? null);
              const label = staged?.label ?? d.cta_effective.label;
              const mapped = targets.get(
                draftMappedTarget(registry, draft, d.id)
              );
              const fallback =
                mapped && !mapped.available && mapped.fallback_target
                  ? targets.get(mapped.fallback_target)
                  : undefined;
              const goesTo = staged
                ? (mapped?.label ?? staged.target)
                : d.cta_effective.source === "override"
                  ? (mapped?.label ?? d.cta_effective.target)
                  : d.cta_default.destination;
              return (
                <tr
                  key={d.id}
                  onClick={() => setEditing(d.id)}
                  className="cursor-pointer align-top hover:bg-brand-faint"
                >
                  <td className="px-4 py-2.5">
                    <div className="font-semibold text-gray-900">{d.title}</div>
                    <div className="font-mono text-[10.5px] text-gray-400">
                      {d.id}
                    </div>
                  </td>
                  <td className="px-3 py-2.5">
                    <span
                      className={`font-semibold ${
                        GENERIC_LABELS.has(label.toLowerCase())
                          ? "text-amber-700"
                          : "text-gray-900"
                      }`}
                    >
                      {label}
                    </span>
                    <div className="text-[11px] text-gray-400">
                      {staged
                        ? "Staged"
                        : appliedOverride
                          ? "Override"
                          : "ExecBridge default"}
                    </div>
                  </td>
                  <td className="px-3 py-2.5 text-gray-600">
                    {goesTo}
                    {mapped && !mapped.available && (
                      <div className="text-[11px] text-amber-700">
                        {fallback
                          ? `Target off → ${fallback.label}`
                          : "Target off — not delivered"}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-gray-600">{d.review}</td>
                  <td className="px-4 py-2.5">
                    {changed ? (
                      <span className="text-[11px] font-semibold text-amber-700">
                        Unapplied
                      </span>
                    ) : (
                      <span className="text-gray-300">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {editingDef && (
        <CtaEditor
          definition={editingDef}
          registry={registry}
          draft={draft}
          onDraftChange={onDraftChange}
          onClose={() => setEditing(null)}
          onEditGuide={onEditGuide}
        />
      )}
    </div>
  );
};

export default CtaPanel;
